'use client';

import React, { useMemo, useState } from 'react';
import {
  CircularProgress,
  Drawer,
  Accordion,
  AccordionItem,
  AccordionSummary,
  AccordionDetails,
} from 'src/components/PulseUI';
import { IconButton } from 'src/components/PulseUI/IconButton';
import { faXmark } from '@fortawesome/pro-solid-svg-icons';
import { useGetMyChainedStrategiesQuery } from 'src/api/redux';
import { LOCATION } from 'src/hooks/filters/constants';
import useStrategies from 'src/hooks/strategy/useStrategies';
import { StrategyInfoSection } from './components';

interface StrategyInfoDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  chainData?: MedScout.ChainedStrategy | null;
  isLoading?: boolean;
  isProfileView?: boolean;
}

/**
 * Unified drawer component for displaying strategy information.
 * Supports two modes:
 * - Multi-strategy (Strategy Center): omit chainData, reads selectedChainIds from Redux
 * - Single-strategy (Profile view): pass chainData prop directly
 */
const StrategyInfoDrawer: React.FC<StrategyInfoDrawerProps> = ({
  isOpen,
  onClose,
  chainData,
  isLoading = false,
  isProfileView = false,
}) => {
  const isSingleStrategyMode = !!chainData || isProfileView;
  const [allExpanded, setAllExpanded] = useState(false);

  // Multi-strategy mode: read from Redux + RTK Query
  const { selectedChainIds } = useStrategies(LOCATION.STRATEGY_CENTER);
  const { data: chainedStrategies, isLoading: isChainsLoading } =
    useGetMyChainedStrategiesQuery(undefined, {
      skip: isSingleStrategyMode,
    });

  // Determine which chains to display
  const chainsToDisplay = useMemo(() => {
    if (chainData) return [chainData];

    if (!chainedStrategies?.results) return [];
    return chainedStrategies.results
      .filter((chain: MedScout.ChainedStrategy) =>
        selectedChainIds.includes(chain.id)
      )
      .sort((a: MedScout.ChainedStrategy, b: MedScout.ChainedStrategy) =>
        (a.label || '').localeCompare(b.label || '')
      );
  }, [chainData, chainedStrategies?.results, selectedChainIds]);


  const isMultiStrategy = !chainData && chainsToDisplay.length > 1;
  const isLoadingState = isLoading || (!chainData && isChainsLoading);
  const showExpandToggle = isMultiStrategy && chainsToDisplay.length >= 3;


  return (
    <Drawer
      isOpen={isOpen}
      onClose={onClose}
      variant="temporary"
      hideBackdrop
      clickOutside={true}
      anchor="right"
      size="lg"
      aria-label="Strategy information drawer"
    >
      <Drawer.Header
        className="bg-sky-800 text-white border-b-0"
        data-testid="strategy-info-header"
      >
        <div className="flex flex-col gap-2 w-full">
          <div className="flex items-center justify-between w-full">
            <div className="flex items-center gap-2">
              <p
                className="font-bold text-lg"
                data-testid="strategy-info-title"
              >
                Strategy Info
              </p>
              {isMultiStrategy && (
                <span
                  className="text-xs font-medium text-sky-100 bg-sky-700 rounded-full px-2 py-0.5"
                  data-testid="strategy-info-count"
                  aria-label={`${chainsToDisplay.length} strategies selected`}
                >
                  {chainsToDisplay.length}
                </span>
              )}
            </div>
            <IconButton
              variant="plain"
              color="neutral"
              size="sm"
              onClick={onClose}
              icon={faXmark}
              className="text-white hover:bg-sky-700"
              data-testid="strategy-info-close-button"
              aria-label="Close strategy info drawer"
            />
          </div>
          <p className="text-sky-200 text-sm font-normal">
            {isMultiStrategy
              ? 'The criteria used to define your selected strategies.'
              : 'The criteria used to define this strategy.'}
          </p>
        </div>
      </Drawer.Header>
      <Drawer.Content className="bg-neutral-50 h-full overflow-y-auto">
        <div className="p-6 pb-12 flex flex-col gap-4">
          {isLoadingState ? (
            <div
              className="flex items-center justify-center min-h-[200px]"
              data-testid="strategy-info-loading"
              aria-label="Loading strategy information"
            >
              <CircularProgress size="md" color="primary" />
            </div>
          ) : chainsToDisplay.length === 0 ? (
            <div
              className="flex items-center justify-center min-h-[200px]"
              data-testid="strategy-info-empty"
            >
              <p className="text-neutral-500 text-sm">
                Strategy information is not available.
              </p>
            </div>
          ) : isMultiStrategy ? (
            <div
              className="flex flex-col gap-3"
              data-testid="strategy-info-accordion"
              role="region"
              aria-label="Strategy details"
            >
              {showExpandToggle && (
                <div className="flex justify-end">
                  <button
                    type="button"
                    onClick={() => setAllExpanded(!allExpanded)}
                    className="text-xs font-medium text-sky-700 hover:text-sky-800 hover:underline"
                    data-testid="expand-collapse-all"
                    aria-label={
                      allExpanded
                        ? 'Collapse all strategies'
                        : 'Expand all strategies'
                    }
                  >
                    {allExpanded ? 'Collapse All' : 'Expand All'}
                  </button>
                </div>
              )}
              {chainsToDisplay.map(
                (chain: MedScout.ChainedStrategy, index: number) => {
                  const defaultOpen = allExpanded || index === 0;

                  return (
                    <Accordion
                      key={`${chain.id}-${allExpanded}`}
                      variant="plain"
                      size="sm"
                      defaultOpen={defaultOpen}
                      className="rounded-xl border border-neutral-200 shadow-sm hover:shadow-md transition-shadow"
                    >
                      <AccordionItem
                        index={0}
                        data-testid={`strategy-accordion-item-${chain.id}`}
                      >
                        <AccordionSummary
                          index={0}
                          className="py-3 px-4 bg-white rounded-xl hover:bg-neutral-50"
                        >
                          <div className="flex flex-col gap-1">
                            <span
                              className="text-sm font-semibold text-primary"
                              data-testid={`strategy-label-${chain.id}`}
                            >
                              {chain.label}
                            </span>
                            {chain.description && (
                              <p
                                className="text-xs text-tertiary line-clamp-1"
                                data-testid={`strategy-description-${chain.id}`}
                              >
                                {chain.description}
                              </p>
                            )}
                          </div>
                        </AccordionSummary>
                        <AccordionDetails
                          index={0}
                          className="border-t border-neutral-100 bg-white rounded-b-xl"
                          contentClassName="px-4 py-4"
                        >
                          <StrategyInfoSection
                            chainData={chain}
                            isProfileView={isProfileView}
                            compact
                          />
                        </AccordionDetails>
                      </AccordionItem>
                    </Accordion>
                  );
                }
              )}
            </div>
          ) : (
            <StrategyInfoSection
              chainData={chainsToDisplay[0]}
              isProfileView={isProfileView}
            />
          )}
        </div>
      </Drawer.Content>
    </Drawer>
  );
};

export default StrategyInfoDrawer;
